import React, { useEffect, useState } from 'react';
import { ChevronDown, Phone,X } from 'lucide-react';
import { VoiceProps } from '../../../campaign/types';
import useRequest from '../../../../../components/hook/use-req';
import Select from '../../../../../components/select';
import Input from '../../../../../components/input';
import Textarea from '../../../../../components/textarea';
import { toast } from 'sonner';
import { Controller, useForm } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import { ClockLoader } from 'react-spinners';
// Types
interface SenderData {
    id: string;
    sender_id: {
        sms: string;
        email: string;
        phone: string;
        whatsapp: string;
    };
    organization_id: string;
    country: string;
    status: string;
    creation_time: string;
    last_modified_time: string;
}

interface OutboundCallForm {
    callHandler: 'AI Agent' | 'Human';
    aiMode: 'Two-Way Conversation' | 'Message & Hang Up';
    sender: string;
    to: string;
    messageType: 'Text-to-Speech' | 'Audio File';
    text: string;
    selectedAgent: string;
    voiceId: string;
    language: string;
}

type CallHandler = 'AI Agent' | 'Human';
type AIMode = 'Two-Way Conversation' | 'Message & Hang Up';
type MessageType = 'Text-to-Speech' | 'Audio File';

// Button Group Component
const ButtonGroup: React.FC<{
    options: string[];
    selected: string;
    onChange: (value: string) => void;
    disabledOptions?: string[];
}> = ({ options, selected, onChange, disabledOptions = [] }) => (
    <div className="flex gap-2">
        {options.map((option) => {
            const isDisabled = disabledOptions.includes(option);
            return (
                <button
                    key={option}
                    onClick={() => !isDisabled && onChange(option)}
                    disabled={isDisabled}
                    className={`flex-1 px-4 py-3 rounded-lg border transition-all ${selected === option
                        ? 'bg-blue-100 border-blue-500 text-blue-700'
                        : isDisabled
                            ? 'bg-gray-100 border-gray-300 text-[#7B7B7B] font-semibold cursor-not-allowed'
                            : 'bg-white border-gray-300 text-gray-700 hover:border-gray-400'
                        }`}
                >
                    {option}
                    {isDisabled && (
                        <span className="ml-2 text-xs bg-gray-200 text-gray-500 px-2 py-1 rounded">
                            COMING SOON
                        </span>
                    )}
                </button>
            );
        })}
    </div>
);

// Info Banner Component
const InfoBanner: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 text-sm text-green-800">
        <span className="font-semibold">AI Agent:</span> {children}
    </div>
);

// Main Component
const MakeOutboundCall: React.FC = () => {
    const token = localStorage.getItem("authToken");
    const [voices, setVoices] = useState<VoiceProps[]>([]);
    const [isAgentDropdownOpen, setIsAgentDropdownOpen] = useState(false);
    const [senders, setSenders] = useState<SenderData[]>([]);
    const [callId, setCallId] = useState<string>('');

    const { makeRequest: senderNames } = useRequest(`/senders/all`, "GET", {
        Authorization: `Bearer ${token}`,
    });
    const { loading, makeRequest: oneVoneCall } = useRequest("/voice/outbound", "POST", {
        Authorization: `Bearer ${token}`,
    });

    // Form controller with default values
    const { control, handleSubmit, watch, setValue, formState: { errors } } = useForm<OutboundCallForm>({
        defaultValues: {
            callHandler: 'AI Agent',
            aiMode: 'Two-Way Conversation',
            sender: '',
            to: '',
            messageType: 'Text-to-Speech',
            text: '',
            selectedAgent: '',
            voiceId: '',
            language: ''
        }
    });

    // Watch form values for conditional rendering
    const callHandler = watch('callHandler');
    const aiMode = watch('aiMode');
    const messageType = watch('messageType');
    const selectedAgent = watch('selectedAgent');

    useEffect(() => {
        const newCallId = uuidv4();
        setCallId(newCallId);
    }, []);


    const handle1v1Call = handleSubmit(async (formData: OutboundCallForm) => {
        // Prepare payload based on form data
        const newCallId = uuidv4();
        setCallId(newCallId);

        const callUpdate = {
            campaign_id: newCallId,
            to: formData.to,
            sender: formData.sender,
            text: formData.text,
            language: formData.language,
            voice_id: formData.voiceId,
        };

        const [response] = await oneVoneCall(callUpdate);
        if (response) {
            toast.success(response.message || "Call initiated successfully!");
            localStorage.removeItem("personalizationLevel");
            localStorage.removeItem("draftCampaignId");
        } else {
            toast.error("Failed to initiate call. Please try again.");
        }
    });

    // Fetch voices data
    useEffect(() => {
        const fetchData = async () => {
            try {
                const res = await fetch("https://voice.dalmlabs.com/voices");
                const data = await res.json();

                if (Array.isArray(data)) {
                    setVoices(data);
                } else if (data && data.data && Array.isArray(data.data)) {
                    setVoices(data.data);
                } else {
                    console.warn("Unexpected response format:", data);
                }
            } catch (error) {
            }
        };

        fetchData();
    }, []);

    // Fetch senders data
    useEffect(() => {
        const fetchData = async () => {
            const [response] = await senderNames();

            if (response && Array.isArray(response)) {
                setSenders(response);
                // Set default from number if available
                if (response.length > 0) {
                    const firstPhone = response.find((sender: SenderData) =>
                        sender.sender_id?.phone && sender.sender_id.phone.trim() !== ""
                    )?.sender_id.phone;
                    if (firstPhone) {
                        setValue('sender', firstPhone);
                    }
                }
            } else if (response && response.data && Array.isArray(response.data)) {
                setSenders(response.data);
                // Set default from number if available
                if (response.data.length > 0) {
                    const firstPhone = response.data.find((sender: SenderData) =>
                        sender.sender_id?.phone && sender.sender_id.phone.trim() !== ""
                    )?.sender_id.phone;
                    if (firstPhone) {
                        setValue('sender', firstPhone);
                    }
                }
            }
        };

        fetchData();
    }, [setValue]);

    // Reset message type to Text-to-Speech when Audio File is disabled
    useEffect(() => {
        if (messageType === 'Audio File') {
            setValue('messageType', 'Text-to-Speech');
        }
    }, [messageType, setValue]);

    // Handle voice selection
    const handleVoiceSelect = (voice: VoiceProps) => {
        const displayName = `${voice.language.toUpperCase()} • ${voice.gender}`;
        setValue('selectedAgent', displayName);
        setValue('voiceId', voice.voice_id);
        setValue('language', voice.language);
        setIsAgentDropdownOpen(false);
    };

    // Audio File is coming soon for both Human and AI Agent
    const getDisabledMessageTypes = () => {
        return ['Audio File'];
    };

    function backtoVoice() {
        window.history.back();
    }

    return (
        <div className="min-h-screen bg-gray-50 flex items-start justify-center p-6">
            <div className="w-full max-w-5xl bg-white rounded-xl shadow-lg">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center">
                            <Phone className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <h1 className="text-xl font-semibold text-gray-900">Make Outbound Call</h1>
                            <p className="text-sm text-gray-500">Initiate a call with custom message and settings</p>
                        </div>
                    </div>
                    <button onClick={backtoVoice} className="text-[#7B7B7B] font-semibold hover:text-gray-600">
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6 max-h-[calc(100vh-200px)] overflow-y-auto">
                    {/* Call Handler */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Call Handler
                        </label>
                        <Controller
                            name="callHandler"
                            control={control}
                            render={({ field }) => (
                                <ButtonGroup
                                    options={['AI Agent', 'Human']}
                                    selected={field.value}
                                    onChange={field.onChange}
                                />
                            )}
                        />
                    </div>

                    {/* AI Mode - Only show when AI Agent is selected */}
                    {callHandler === 'AI Agent' && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                AI Mode
                            </label>
                            <Controller
                                name="aiMode"
                                control={control}
                                render={({ field }) => (
                                    <ButtonGroup
                                        options={['Two-Way Conversation', 'Message & Hang Up']}
                                        selected={field.value}
                                        onChange={field.onChange}
                                    />
                                )}
                            />
                        </div>
                    )}

                    {/* AI Personality - Only show when AI Agent is selected */}
                    {callHandler === 'AI Agent' && (
                        <div className="relative w-1/2">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                AI Personality
                            </label>
                            <button
                                type="button"
                                onClick={() => setIsAgentDropdownOpen(!isAgentDropdownOpen)}
                                className="w-full p-3 border border-gray-300 rounded-lg text-left flex items-center justify-between hover:border-gray-400 transition-colors"
                            >
                                <span className={selectedAgent ? "text-gray-900" : "text-[#7B7B7B] font-semibold"}>
                                    {selectedAgent || "Choose an AI voice..."}
                                </span>
                                <ChevronDown className="w-5 h-5 text-[#7B7B7B] font-semibold" />
                            </button>

                            {isAgentDropdownOpen && (
                                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                    {voices.length > 0 ? (
                                        voices.map((voice) => (
                                            <button
                                                key={voice.voice_id}
                                                type="button"
                                                onClick={() => handleVoiceSelect(voice)}
                                                className="w-full p-3 text-left hover:bg-blue-50 transition-colors text-sm"
                                            >
                                                <div className="flex justify-between items-center">
                                                    <span>{voice.language.toUpperCase()}</span>
                                                    <span className="capitalize text-gray-500">{voice.gender}</span>
                                                </div>
                                            </button>
                                        ))
                                    ) : (
                                        <p className="p-3 text-[#7B7B7B] font-semibold text-sm">No voices available</p>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Phone Numbers */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <Controller
                                name="sender"
                                control={control}
                                rules={{ required: "From number is required" }}
                                render={({ field, fieldState }) => (
                                    <Select
                                        label="From Number"
                                        value={field.value}
                                        onChange={field.onChange}
                                        options={
                                            senders
                                                .filter((sender) => sender.sender_id?.phone && sender.sender_id.phone.trim() !== "")
                                                .map((sender) => ({
                                                    value: sender.sender_id.phone,
                                                    label: sender.sender_id.phone,
                                                }))
                                        }
                                        error={fieldState.error?.message}
                                    />
                                )}
                            />
                        </div>
                        <div>
                            <Controller
                                name="to"
                                control={control}
                                rules={{
                                    required: "To number is required",
                                    pattern: {
                                        value: /^\+?[\d\s-]+$/,
                                        message: "Please enter a valid phone number"
                                    }
                                }}
                                render={({ field, fieldState }) => (
                                    <Input
                                        value={field.value}
                                        onChange={field.onChange}
                                        placeholder="+91 9876543211"
                                        label='To Number'
                                        error={fieldState.error?.message}
                                    />
                                )}
                            />
                        </div>
                    </div>

                    {/* Message Type */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Message Type
                        </label>
                        <Controller
                            name="messageType"
                            control={control}
                            render={({ field }) => (
                                <ButtonGroup
                                    options={['Text-to-Speech', 'Audio File']}
                                    selected={field.value}
                                    onChange={field.onChange}
                                    disabledOptions={getDisabledMessageTypes()}
                                />
                            )}
                        />
                        <p className="text-sm text-gray-500 mt-2">
                            Audio File feature is coming soon for both AI Agent and Human call handlers.
                        </p>
                    </div>

                    {/* Message */}
                    {messageType === 'Text-to-Speech' ? (
                        <div>
                            <Controller
                                name="text"
                                control={control}
                                rules={{
                                    required: "Message is required",
                                    minLength: {
                                        value: 10,
                                        message: "Message should be at least 10 characters long"
                                    }
                                }}
                                render={({ field, fieldState }) => (
                                    <Textarea
                                        label='Message'
                                        value={field.value}
                                        onChange={field.onChange}
                                        placeholder="Enter your message here. It will be converted to speech..."
                                        error={fieldState.error?.message}
                                    />
                                )}
                            />
                        </div>
                    ) : (
                        <div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Audio File URL
                                </label>
                                <input
                                    type="text"
                                    placeholder="https://your-domain.com/audio/broadcast.mp3"
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                                    disabled
                                />
                                <div className='flex justify-between mt-3 opacity-25'>
                                    <span>
                                        Supported formats: MP3, WAV (max 10MB)
                                    </span>
                                    <button type="button" className="text-blue-600 text-sm mt-2 hover:underline" disabled>
                                        Preview Audio
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Info Banner - Only show when AI Agent is selected */}
                    {callHandler === 'AI Agent' && (
                        <InfoBanner>
                            Will conduct a two-way conversation and respond to customer queries. Supports Hindi, English, and regional languages.
                        </InfoBanner>
                    )}
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-gray-200">
                    <button
                        onClick={handle1v1Call}
                        className="w-full bg-gradient-to-r from-blue-500 to-blue-500 text-white py-3 rounded-lg font-medium flex items-center justify-center gap-2 hover:from-blue-600 hover:to-blue-600 transition-all"
                    >
                        {loading ? (
                            <ClockLoader color="#ffffff" loading={loading} size={20} />
                        ) : (
                            <>
                                <Phone className="w-5 h-5" />
                                Make Call Now
                            </>
                        )}

                    </button>
                </div>
            </div>
        </div>
    );
};

export default MakeOutboundCall;